import { createEffect, createSignal, JSX, Show } from 'solid-js';
import styles from './import-wizard.module.css';
import { reportBrowser as api } from 'st-app/api'
import { blobToBase64, toast } from 'common';
import { ReportBrowserContext, requireReportBrowser } from '../report-browser-context';
import STCheckbox from '../../st-checkbox/st-checkbox';
import STInput from '../../st-input/st-input';
import Button from '../../st-button/st-button';
import { useModalContext } from '../../st-modal/st-modal';

export const ImportWizard = (props: { }) => {
    const modal = useModalContext();
    const reportBrowser = requireReportBrowser();

    const [file, setFile] = createSignal<File | null>(null);
    const [isImporting, setIsImporting] = createSignal(false);
    const [overwriteExisting, setOverwriteExisting] = createSignal(false);

    const [newReportName, setNewReportName] = createSignal('');
    const [newReportNumber, setNewReportNumber] = createSignal('');
    const [useNameFromArchive, setUseNameFromArchive] = createSignal(true);

    const importArchive = async () => {
        const f = file();
        if (!f) return;

        setIsImporting(true);
        try {
            const base64Url = await blobToBase64(f);
            const base64 = base64Url.split(',')[1];
            const reportGu = await api.ImportReport(base64, {
                overwriteExisting: overwriteExisting(),
                newReportName: newReportName() || undefined,
                newReportNumber: newReportNumber() || undefined,
            });

            modal?.close();

            if (reportGu && reportBrowser) {
                await reportBrowser.refresh();
                reportBrowser.focusReport(reportGu);
            }

            toast.notify('success', 'Report imported successfully.');
        } catch (e) {
            console.error(e);
            if (typeof e === 'string') {
                toast.notify('error', e);
            } else if (e && typeof e === 'object' && 'message' in e) {
                toast.notify('error', (e as any).message);
            } else {
                toast.notify('error', 'An unknown error occurred during import.');
            }
        } finally {
            setIsImporting(false);
        }
    }

    return (
        <div class={styles['import-wizard']}>
            <Show when={!file()}>
                <FileUploadArea
                    style={{
                        width: '400px',
                        height: '200px',
                    }}
                    onFileSelect={(f) => setFile(f)}
                    accept='application/zip'
                />
            </Show>
            <Show when={file()}>
                <div class={styles['file-info']}>
                    <span class="fa fa-solid fa-file-zipper" style="font-size:20px;"></span>
                    {file() ? `${file()!.name}` : 'No file selected'}
                </div>

                <div class={styles['import-options']}>
                    <STCheckbox label='Overwrite existing report' value={[overwriteExisting, setOverwriteExisting]} />
                    <STCheckbox label='Use name/number from archive' value={[useNameFromArchive, setUseNameFromArchive]} />

                    <Show when={!useNameFromArchive()}>
                        <div class={styles['import-name-options']}>
                            <div class={styles['import-option']}>
                                <STInput focused required label='Number' value={[newReportNumber, setNewReportNumber]} />
                            </div>
                            <div class={styles['import-option']}>
                                <STInput required label='Name' value={[newReportName, setNewReportName]} />
                            </div>
                        </div>
                    </Show>
                </div>

                <div class={styles['import-actions']}>
                    <Button onClick={() => setFile(null)} enabled={!isImporting()}>
                        Choose A Different File
                    </Button>
                    <Button type='primary' onClick={importArchive} enabled={!!file()} loading={isImporting() && !!file()}>
                        {isImporting() ? 'Importing...' : 'Import'}
                    </Button>
                </div>
            </Show>
        </div>
    )
}


const FileUploadArea = (props: {
    style?: JSX.CSSProperties
    onFileSelect?: (file: File) => void
    accept?: string
}) => {
    let fileInput: HTMLInputElement = undefined!;
    const [isDragging, setIsDragging] = createSignal(false);

    const triggerFileInput = () => {
        fileInput.click();
    }

    return (
        <div
            class={styles['file-upload-area']}
            onClick={triggerFileInput}
            style={props.style}
            ondragover={(e) => {
                e.preventDefault();
                setIsDragging(true);
            }}
            ondragleave={() => setIsDragging(false)}
            ondrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                if (e.dataTransfer?.files && e.dataTransfer.files.length > 0 && props.onFileSelect) {
                    props.onFileSelect(e.dataTransfer.files[0]);
                }
            }}

            data-dragging={isDragging() ? true : undefined}
        >
            <div class={styles['file-upload-text']}>
                Click or Drag files here to upload
            </div>

            <input
                ref={fileInput}
                type="file"
                multiple
                hidden
                aria-hidden='true'
                onChange={(e) => {
                    const files = e.currentTarget.files;
                    if (files && files.length > 0 && props.onFileSelect) {
                        props.onFileSelect(files[0]);
                    }
                    e.currentTarget.value = '';
                }}
                accept={props.accept}
            />
        </div>
    )
}